import { useCallback, useState } from 'react'
import { useApp, useNow } from '../store/AppContext'
import { useDismiss } from '../lib/hooks'
import { ROLE_LABEL, type Role, type Screen } from '../lib/types'
import { initials } from '../lib/util'

export const SCREENS: ReadonlyArray<readonly [Screen, string, string]> = [
  ['timer', 'Timer', '◷'],
  ['prospects', 'Prospects', '◴'],
  ['timesheet', 'Timesheet', '▦'],
  ['reports', 'Reports', '◱'],
  ['projects', 'Projects', '◆'],
  ['clients', 'Clients', '◇'],
  ['team', 'Team', '☺'],
  ['settings', 'Settings', '⚙'],
]

export const TITLES: Record<Screen, [string, string]> = {
  timer: ['Timer', 'Track as you go, or log it after the fact.'],
  prospects: ['Prospects', 'Time spent chasing work that is not won yet.'],
  timesheet: ['Timesheet', 'A week at a glance — type hours straight into the grid.'],
  reports: ['Reports', 'Where the hours went, and what they were worth.'],
  projects: ['Projects', 'Budgets, rates and how far through you are.'],
  clients: ['Clients', 'Who the work belongs to.'],
  team: ['Team', 'Capacity and utilisation across the workspace.'],
  settings: ['Settings', 'Workspace rules and your own preferences.'],
}

/**
 * Everyone sees every screen.
 *
 * Team and Clients used to be hidden from members, which stopped being
 * defensible once members could create clients -- the permission existed with
 * nowhere to use it. What each person may DO is decided by RLS and by the
 * controls each screen renders, not by whether the nav item is there: hiding
 * a link was never the security boundary, and pretending otherwise just meant
 * people could not see who their colleagues were.
 */
export function visibleScreens(_role: Role | undefined) {
  return SCREENS
}

/* ------------------------------------------------------------------ */

function WorkspaceMenu({ onDark }: { onDark?: boolean }) {
  const { auth, ws, selectWorkspace, setScreen } = useApp()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useDismiss<HTMLDivElement>(open, close)
  const list = auth?.workspaces ?? []

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0, flex: 1 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch workspace"
        style={{
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'left',
          minWidth: 0,
          width: '100%',
          color: 'inherit',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-.01em' }}>Cadence</div>
        <div className="trunc" style={{ fontSize: 11, color: onDark ? 'var(--railMut)' : 'var(--mut)' }}>
          {ws?.name ?? '—'} ▾
        </div>
      </button>

      {open && (
        <div className="popover popover--down" style={{ left: 0, width: 240, padding: 6 }} role="menu">
          <div className="eyebrow" style={{ padding: '6px 10px 4px' }}>
            Workspaces
          </div>
          {list.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              className="popover-row"
              aria-selected={w.id === ws?.id}
              onClick={() => {
                selectWorkspace(w.id)
                setOpen(false)
              }}
            >
              <span className="trunc" style={{ flex: 1 }}>
                {w.name}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>{ROLE_LABEL[w.role]}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--line2)', margin: '6px 0' }} />
          <button
            type="button"
            role="menuitem"
            className="popover-row"
            onClick={() => {
              setScreen('settings')
              setOpen(false)
            }}
          >
            <span style={{ color: 'var(--ac-ink)', fontWeight: 600 }}>+ Create or join another</span>
          </button>
        </div>
      )}
    </div>
  )
}

function SignOutButton({ className, style }: { className: string; style?: React.CSSProperties }) {
  const { confirm, signOut } = useApp()
  return (
    <button
      type="button"
      className={className}
      style={style}
      title="Sign out"
      aria-label="Sign out"
      onClick={() =>
        confirm({
          title: 'Sign out?',
          body: 'Your time stays on the server. Sign back in from any browser to pick up where you left off.',
          cta: 'Sign out',
          onConfirm: () => void signOut(),
        })
      }
    >
      ⏻
    </button>
  )
}

/* ------------------------------------------------------------------ */

export function Sidebar({ weekTotal }: { weekTotal: string }) {
  const { ws, me, role, screen, setScreen, running, setModal } = useApp()
  if (!ws || !me) return null

  return (
    <aside className="rail">
      <div className="rail-brand">
        <div className="brandmark">C</div>
        <WorkspaceMenu onDark />
      </div>

      <nav className="rail-nav" aria-label="Main">
        {visibleScreens(role).map(([id, label, glyph]) => {
          const badge =
            id === 'timer' && running
              ? '●'
              : id === 'projects'
                ? String(ws.projects.filter((p) => p.status === 'active').length)
                : ''
          return (
            <button
              key={id}
              type="button"
              className="navitem"
              aria-current={screen === id ? 'page' : undefined}
              onClick={() => setScreen(id)}
            >
              <span className="glyph" aria-hidden>
                {glyph}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
              {badge && (
                <span className="badge" style={id === 'timer' ? { color: 'var(--ac)' } : undefined}>
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      <div className="rail-foot">
        <button
          type="button"
          className="avatar-sm"
          style={{ border: 0, cursor: 'pointer' }}
          title="Your profile"
          onClick={() => setModal({ kind: 'profile' })}
        >
          {initials(me.name)}
        </button>
        <div className="minw0" style={{ flex: 1 }}>
          <div className="trunc" style={{ fontSize: 13, fontWeight: 500 }}>
            {me.name}
          </div>
          <div className="trunc" style={{ fontSize: 11, color: 'var(--railMut)' }}>
            {ROLE_LABEL[me.role]} · {weekTotal} this week
          </div>
        </div>
        <SignOutButton className="ibtn" style={{ color: 'var(--railMut)', width: 26, height: 26 }} />
      </div>
    </aside>
  )
}

export function Topnav({ weekTotal }: { weekTotal: string }) {
  const { ws, me, role, screen, setScreen, setModal } = useApp()
  if (!ws || !me) return null

  return (
    <header className="topnav">
      <div className="row" style={{ gap: 9, flexShrink: 0, width: 170 }}>
        <div className="brandmark">C</div>
        <WorkspaceMenu onDark />
      </div>
      <nav style={{ display: 'flex', gap: 2, flex: '1 1 auto' }} aria-label="Main">
        {visibleScreens(role).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="topnav-item"
            aria-current={screen === id ? 'page' : undefined}
            onClick={() => setScreen(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="row" style={{ gap: 10, flexShrink: 0 }}>
        <div className="mono" style={{ fontSize: 12.5, color: 'var(--railMut)', whiteSpace: 'nowrap' }}>
          {weekTotal} this week
        </div>
        <button
          type="button"
          className="avatar-sm"
          style={{ border: 0, cursor: 'pointer' }}
          title={`${me.name} — your profile`}
          onClick={() => setModal({ kind: 'profile' })}
        >
          {initials(me.name)}
        </button>
        <SignOutButton className="ibtn" style={{ color: 'var(--railMut)', width: 26, height: 26 }} />
      </div>
    </header>
  )
}

/**
 * Wall clock, ticking every second.
 *
 * It reads `now` from the app context rather than starting its own interval:
 * the running timer already ticks once a second there, so this costs nothing
 * extra and the two can never disagree about what time it is.
 */
function LiveClock() {
  const now = useNow()
  const { ws } = useApp()
  const use24 = ws?.settings.timeFormat === '24h'
  const d = new Date(now)

  const time = d.toLocaleTimeString(undefined, {
    hour: use24 ? '2-digit' : 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: !use24,
  })
  const date = d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className="stat-pill" style={{ minWidth: 0 }}>
      <b className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {time}
      </b>
      <span className="eyebrow" style={{ whiteSpace: 'nowrap' }}>
        {date}
      </span>
    </div>
  )
}

export function PageHead({ today, week }: { today: string; week: string }) {
  const { screen } = useApp()
  const [title, sub] = TITLES[screen]
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        <p>{sub}</p>
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <LiveClock />
        <div className="stat-pill">
          <span className="eyebrow">Today</span>
          <b>{today}</b>
        </div>
        <div className="stat-pill">
          <span className="eyebrow">Week</span>
          <b>{week}</b>
        </div>
      </div>
    </div>
  )
}

export function Toast() {
  const { toast } = useApp()
  if (!toast) return null
  return (
    <div className="toast" role="status" aria-live="polite">
      {toast}
    </div>
  )
}
